// RSVPSubmit.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, useParams, Link } from 'react-router-dom';
import * as utils from './Utils.js';
import { db } from '../firebase.js';
import LoadingAnimation from './LoadingAnimation.js';
import { Helmet } from 'react-helmet-async';

const RSVPSubmit = () => {
  const { reservation_id } = useParams();
  const navigate = useNavigate();

  const [reservation, setReservation] = useState({ id: '', name: '', lodging: null });
  const [guests, setGuests] = useState([]);
  const [originalGuests, setOriginalGuests] = useState([]);
  const [originalReservation, setOriginalReservation] = useState(null);
  const [events, setEvents] = useState([]);
  const [invitedEvents, setInvitedEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(0);
  const [responses, setResponses] = useState({});
  const [pageState, setPageState] = useState('main-rsvp');
  const [lodgingOptionSelected, setLodgingOptionSelected] = useState(false)
  const [isDoneLoading, setIsDoneLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notBookedYet, setNotBookedYet] = useState(false)
  const [otherHotelSelected, setOtherHotelSelected] = useState(false)
  const [weddingCalendarName, setWeddingCalendarName] = useState('wedding-weekend')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventsRef = collection(db, 'events');
        const eventsSnap = await getDocs(eventsRef);
        const eventsList = [];
        eventsSnap.forEach((d) => eventsList.push({ id: d.id, ...d.data() }));
        eventsList.map((d) => {
          d.start = d.start?.toDate()
          d.end = d.end?.toDate()
          return d;
        });
        eventsList.sort((a, b) => {
          return a.start - b.start;
        });
        setEvents(eventsList);

        const reservationRef = doc(db, 'reservations', reservation_id);
        const reservationSnap = await getDoc(reservationRef);
        if (!reservationSnap.exists()) {
          navigate('/rsvp');
          return;
        }

        try {
          if (process.env.NODE_ENV === 'production') {
            logRSVPView(reservation_id);
          }
        } catch (err) {
          console.log("Didn't log page view:", err);
        }


        const resrvtn = { id: reservationSnap.id, ...reservationSnap.data() }
        setReservation(resrvtn);
        setOriginalReservation(JSON.parse(JSON.stringify(resrvtn)));

        const guestsRef = collection(db, 'guests');
        const q = query(guestsRef, where('reservation_id', '==', reservation_id));
        const guestsSnap = await getDocs(q);
        const guestList = [];
        guestsSnap.forEach((d) =>
          guestList.push({
            id: d.id,
            ...d.data(),
            name: d.data().name === '' ? 'Guest' : d.data().name
          })
        );

        setGuests(guestList);
        setOriginalGuests(JSON.parse(JSON.stringify(guestList)));

        // Build unique list of event ids for this reservation
        const eventIdSet = new Set();
        guestList.forEach((guest) => {
          guest.events?.forEach((ev) => {
            eventIdSet.add(ev.id);
          });
        });

        // sort eventIdSet by eventList order
        const sortedEventIds = Array.from(eventIdSet).sort((a, b) => {
          const aIndex = eventsList.findIndex(e => e.id === a);
          const bIndex = eventsList.findIndex(e => e.id === b);
          return aIndex - bIndex;
        });

        let invEvs = Array.from(sortedEventIds)

        setInvitedEvents(invEvs);
        setCurrentEvent(0);

        if (invEvs.includes('bridal_party_tea')) {
          if (invEvs.includes('ceremony_rehearsal')) {
            setWeddingCalendarName('wedding-weekend-all')
          } else {
            setWeddingCalendarName('wedding-weekend-without-rehearsal')
          }
        } else if (invEvs.includes('ceremony_rehearsal')) {
          setWeddingCalendarName('wedding-weekend-with-rehearsal')
        } else if (invEvs.includes('rehearsal_dinner')) {
          setWeddingCalendarName('wedding-weekend-family')
        } else {
          setWeddingCalendarName('wedding-weekend')
        }


        const initialResponses = {};

        guestList.forEach((g) => {
          g.events.forEach((ev) => {
            const key = `${g.id}_${ev.id}`;

            initialResponses[key] = {
              rsvp: ev.rsvp ?? 'pending',
              meal_id: ev.meal_id ?? ''
            };
          });
        });

        guestList.forEach((guest) => {
          const eventRSVPs = guest.events.map(
            (ev) => ev.rsvp ?? 'pending'
          );

          if (eventRSVPs.every(r => r === 'yes' || r === 'no')) {
            initialResponses[`${guest.id}_main_rsvp`] = {
              rsvp: eventRSVPs.every(r => r === 'no') ? 'no' : 'yes'
            };
          }
        });

        setResponses(initialResponses);

        const rsvpAlreadySubmitted = invEvs.length > 0 && invEvs.every(eventId => {
          const guestsWithEvent = guestList.filter(g => g.events.some(e => e.id === eventId));
          const eventDetails = eventsList.find(e => e.id === eventId);

          return guestsWithEvent.length > 0 && guestsWithEvent.every(g => {
            const key = `${g.id}_${eventId}`;
            const resp = initialResponses[key];
            
            if (!resp?.rsvp || resp?.rsvp == 'pending') return false;
            
            if (eventDetails?.rsvpType === 'meal' && resp.rsvp === 'yes' && g.type !== 'child') {
              return !!resp.meal_id;
            }

            return true;
          });
        });

        if (rsvpAlreadySubmitted) {
          setPageState('reservation-review')
          if (resrvtn?.lodging?.id && resrvtn?.lodging?.name && resrvtn?.lodging?.address) {
            setLodgingOptionSelected(true)
            if (resrvtn?.lodging?.id == 'hotel' && !hotelOptions.map(m => m.name).includes(resrvtn?.lodging?.name)) {
              setOtherHotelSelected(true)
            }
          } else {
            if (resrvtn?.lodging?.id != null && resrvtn?.lodging?.name != null && resrvtn?.lodging?.address != null) {
              setNotBookedYet(true)
            }
            else {
              setLodgingOptionSelected(false)
            }
          }
        }

        setIsDoneLoading(true)
      } catch (err) {
        console.error('Error loading reservation:', err);
      }
    };

    if (reservation_id) {
      fetchData();
    }
  }, [reservation_id, navigate]);

  useEffect(() => {
    if (reservation?.lodging?.id && reservation?.lodging?.name && reservation?.lodging?.address) {
      setLodgingOptionSelected(true)
    } else {
      setLodgingOptionSelected(false)
    }
  }, [reservation])

  const saveResponse = (key, updates) => {
    if (key.includes('main_rsvp')) {
      let guestID = key.split('_main_rsvp')[0]
      let guestKeys = Object.keys(responses).filter((k) => k.startsWith(guestID))

      if (updates.rsvp === 'no') {
        for (let kIndex in guestKeys) {
          let k = guestKeys[kIndex]
          setResponses(prev => ({
            ...prev,
            [k]: { ...prev[k], ...updates }
          }));
        }
      } else if (updates.rsvp === 'yes') {
        for (let kIndex in guestKeys) {
          let k = guestKeys[kIndex]
          setResponses(prev => ({
            ...prev,
            [k]: { ...prev[k], ...{ rsvp: 'pending' } }
          }));
        }
      }
    }
    setResponses(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  };

  const logRSVPView = async (reservation_id) => {
    try {
      await addDoc(collection(db, 'rsvp_page_views'), {
        reservation_id,
        viewed_at: serverTimestamp(),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        referrer: document.referrer || null
      });
    } catch (err) {
      console.error('Failed to log RSVP view', err);
    }
  };


  const currentEventId = invitedEvents[currentEvent];
  const eventDetails = events.find(e => e.id === currentEventId);

  const submitAll = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const submitterInfo = {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || null
      };

      for (const guest of guests) {
        console.log(guest)
        const guestRef = doc(db, 'guests', guest.id);
        const updatedEvents = guest.events.map((ev) => {
          const key = `${guest.id}_${ev.id}`;
          const resp = responses[key];
          if (resp) {
            return {
              ...ev,
              rsvp: resp.rsvp || ev.rsvp,
              meal_id: resp.meal_id || ev.meal_id || ''
            };
          }
          return ev;
        });

        const updatedGuest = {
          ...guest,
          events: updatedEvents
        };
        
        await updateDoc(guestRef, {
          dietary_restrictions: updatedGuest.dietary_restrictions,
          name: updatedGuest.name,
          events: updatedGuest.events
        });
        
        const originalGuest = originalGuests.find(g => g.id === guest.id);
        const diff = originalGuest ? diffGuest(originalGuest, updatedGuest) : null;

        if (diff) {
          await addDoc(collection(db, 'rsvp_logs'), {
            reservation_id,
            guest_id: guest.id,
            guest_name: updatedGuest.name || '',
            diff,
            submitted_at: serverTimestamp(),
            submitter: submitterInfo
          });
        }
      }

      if (reservation?.lodging) {
        const reservationRef = doc(db, 'reservations', reservation_id);

        const lodgingChanged =
          JSON.stringify(originalReservation?.lodging || {}) !==
          JSON.stringify(reservation.lodging || {});

        if (lodgingChanged) {
          await updateDoc(reservationRef, {
            lodging: reservation.lodging
          });

          await addDoc(collection(db, 'rsvp_logs'), {
            reservation_id,
            guest_id: reservation_id,
            guest_name: reservation.name || '',
            diff: {
              lodging: {
                from: originalReservation?.lodging || null,
                to: reservation.lodging
              }
            },
            submitted_at: serverTimestamp(),
            submitter: submitterInfo
          });

          setOriginalReservation(JSON.parse(JSON.stringify(reservation)));
        }
      }

      setPageState('reservation-review');
    } catch (err) {
      console.error('Error saving RSVPs:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveGuestName = async () => {
    const updatedGuests = guests.map((guest) => {
      const input = document.getElementById(`guest_id_${guest.id}_name_input`);
      if (input) {
        let nameToSave = input.value === '' ? 'Guest' : input.value
        return { ...guest, name: nameToSave };
      }
      return guest;
    });
    setGuests(updatedGuests);
  }
  const saveGuestDietaryRestrictions = async () => {
    const updatedGuests = guests.map((guest) => {
      const input = document.getElementById(`guest_id_${guest.id}_dietary_restrictions_input`);
      if (input) {
        return { ...guest, dietary_restrictions: input.value };
      }
      return guest;
    });
    setGuests(updatedGuests);
  }

  const diffGuest = (oldGuest, newGuest) => {
    const changes = {};

    // name diff
    if ((oldGuest.name || '') !== (newGuest.name || '')) {
      changes.name = {
        from: oldGuest.name || '',
        to: newGuest.name || ''
      };
    }

    // dietary restrictions diff
    if ((oldGuest.dietary_restrictions || '') !== (newGuest.dietary_restrictions || '')) {
      changes.dietary_restrictions = {
        from: oldGuest.dietary_restrictions || '',
        to: newGuest.dietary_restrictions || ''
      };
    }

    // events diff
    const eventChanges = [];
    for (const newEv of newGuest.events) {
      const oldEv = oldGuest.events.find(e => e.id === newEv.id);
      if (!oldEv) continue;

      const evDiff = {};
      if (oldEv.rsvp !== newEv.rsvp) {
        evDiff.rsvp = { from: oldEv.rsvp ?? null, to: newEv.rsvp ?? null };
      }
      if ((oldEv.meal_id || '') !== (newEv.meal_id || '')) {
        evDiff.meal_id = { from: oldEv.meal_id || null, to: newEv.meal_id || null };
      }

      if (Object.keys(evDiff).length > 0) {
        eventChanges.push({
          event_id: newEv.id,
          changes: evDiff
        });
      }
    }

    if (eventChanges.length > 0) {
      changes.events = eventChanges;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  };

  const allEventsCompleted = invitedEvents.length > 0 && invitedEvents.every(eventId => {
    const guestsWithEvent = guests.filter(g => g.events.some(e => e.id === eventId));
    const eventDetails = events.find(e => e.id === eventId);

    return guestsWithEvent.length > 0 && guestsWithEvent.every(guest => {
      const key = `${guest.id}_${eventId}`;
      const resp = responses[key];
      
      if (!resp?.rsvp || resp?.rsvp == 'pending') return false;
      
      if (eventDetails?.rsvpType === 'meal' && resp.rsvp === 'yes' && guest.type !== 'child') {
        return !!resp.meal_id;
      }

      return true;
    });
  });

  const updateMainRSVP = () => {
    guests.map((guest) => {
      if (responses[`${guest.id}_main_rsvp`].rsvp == 'yes') {
        // no-op
      } else {
        // make all responses no for this guest
        invitedEvents.map((eventId) => {
          const key = `${guest.id}_${eventId}`;
          saveResponse(key, { rsvp: 'no', meal_id: '' });
        });
        guest.dietary_restrictions = ''
      }
    })

    const anyGuestsPotentiallyComingForReservation = guests.some((g) => {
      return g.type === 'guest' && responses[`${g.id}_main_rsvp`]?.rsvp !== 'no'
    })

    guests.every((g) => ['no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp))

    if (anyGuestsPotentiallyComingForReservation) {
      setPageState('guest');
    } else {
      setCurrentEvent(0)
      setPageState('rsvp');
    }
  }

  const setEventView = (index) => {
    if(!guests.every((g) => ['yes', 'no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp))) {
      return;
    }
    setPageState('rsvp')
    setCurrentEvent(index)
  }

  const setAdditionalQuestionsView = () => {
    if(!guests.every((g) => ['yes', 'no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp))) {
      return;
    }
    setPageState('additional-questions')
  }

  const backButtonClick = () => {
    if (pageState === 'guest') {
      setPageState('main-rsvp')
    } else if (pageState === 'rsvp') {
      if (currentEvent === 0) {
        setPageState('main-rsvp')
      } else {
        setCurrentEvent(i => i - 1)
      }
    } else if (pageState === 'additional-questions') {
      setCurrentEvent(invitedEvents.length - 1)
      setPageState('rsvp')      
    }
  }

  const nextButtonClick = () => {
    if (pageState === 'main-rsvp') {
      updateMainRSVP()
    } else if (pageState === 'rsvp') {
      if (currentEvent === invitedEvents.length - 1) {
        setAdditionalQuestionsView()
      } else {
        setCurrentEvent(i => i + 1)
      }
    } else if (pageState === 'guest') {
      setCurrentEvent(0)
      setPageState('rsvp')
    }
  }

  const reservationRSVPNo = guests.every((g) => { return responses[`${g.id}_main_rsvp`]?.rsvp === 'no' })

  const readyToSubmit = reservationRSVPNo || (allEventsCompleted && (lodgingOptionSelected || notBookedYet))

  const hotelOptions = [
    {
      id: 'newport_marriott_hotel',
      name: 'Newport Marriott Hotel',
      address: "25 America's Cup Avenue, Newport, RI, 02840"
    },
    {
      id: 'newport_harbor_hotel',
      name: 'Newport Harbor Hotel',
      address: "49 America's Cup Avenue, Newport, RI, 02840"
    },
    {
      id: 'gardiner_house',
      name: 'Gardiner House',
      address: "24 Lee's Wharf, Newport, RI, 02840"
    },
    {
      id: 'wayfinder_newport',
      name: 'Wayfinder Newport',
      address: "151 Admiral Kalbfus Road, Newport, RI, 02840"
    },
    {
      id: 'sonesta_select',
      name: 'Sonesta Select Middletown',
      address: "9 Commerce Drive, Middletown, RI, 02842"
    },
    { id: 'other', name: 'Other', address: ''
    }
  ]
  
  const lodgingOptions = [
    { id: 'hotel', name: 'Hotel', address: '' },
    { id: 'rental', name: 'AirBnB / Rental', address: '' },
    { id: 'other', name: 'Other', address: '' }
  ]

  return (
    <>
      <Helmet>
        <title>RSVP</title>
        <meta
          name="description"
          content="Respond to your Wedding invitation!"
        />

        <meta property="og:title" content="RSVP | Chandler & Anna" />
        <meta
          property="og:description"
          content="Respond to your Wedding invitation!"
        />
        <meta
          property="og:image"
          content="https://chandlerandanna.com/og/rsvp.jpg"
        />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="400" />
        <meta property="og:url" content="https://chandlerandanna.com/rsvp" />

        <meta name="twitter:card" content="summary_large_image" />
        
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {!isDoneLoading ? (
        <div className="RSVP">
          <div className='centered-box' style={{minHeight: '410px', justifyContent: 'center'}}>
            <div className='main-wedding-centerpiece'>
              <LoadingAnimation/>
            </div>
          </div>
          <div className='centered-box'>
            { utils.displayCheckerboard(50, 42, ['green', 'ivory']) }
          </div>
        </div>
      ) : (
        <div className="RSVP">
          <div className='wedding-page-blocks'>

            <div className='centered-box wedding-page-block'>
              <div className='wedding-centerpiece'>
                <p className='wedding-rsvp-title'>{reservation.name}</p>
                  { pageState === 'reservation-review' && !reservationRSVPNo ?
                    <a class="btn btn-outline-navyblue" href={`/calendars/${weddingCalendarName}.ics`} download={`${weddingCalendarName}.ics`} style={{marginTop: '20px'}}>
                      Add all events to calendar
                    </a> : <></>
                  }
              </div>

              { pageState === 'reservation-review' && (
              <div className="centered-box wedding-page-block wedding-form-container review-thank-you-page-centered">
                <div className='main-wedding-centerpiece review-thank-you-page'>
                  <div className='rsvp-card-event'>
                    { reservationRSVPNo ?
                    <>
                      <h4>We will miss you!</h4>
                      <p className='rsvp-info'>We're sad you won't be able to join us for our wedding. Please keep us in your prayers as we begin married life. Know of our prayers for you as well.</p>
                      <p className='from-chandler-and-anna'>&mdash; Chandler and Anna</p>
                    </>
                    :
                    <>
                      <h4>Thanks for submitting your RSVP!</h4>
                    </>
                    }
                    
                    { reservationRSVPNo || reservation?.lodging?.id ? <></> : <><p className='rsvp-info'>See our <Link target="_blank" to="/hotel-and-travel" className='link-to-page'>Hotels</Link> page for links to secure a room in our hotel blocks. Once you have booked your stay, come back and edit your RSVP.</p></> }
                    { reservationRSVPNo ? <></> : <p className='rsvp-info'>Find places to see, restaurants, and bars on our <Link target="_blank" to="/things-to-do" className='link-to-page'>Things To Do</Link> page.</p> }
                    { reservationRSVPNo ? <></> : <p className='rsvp-info'>Questions? See our <Link target="_blank" to="/things-to-do" className='link-to-page'>FAQ</Link> page.</p> }
                    
                    <button className="btn btn-navyblue" style={{marginTop: '20px'}} onClick={() => setPageState('main-rsvp')}>
                      Edit RSVP
                    </button>
                  </div>
                </div>
              </div>
              )}

              { pageState !== 'reservation-review' && (
                <>
                  <div className='event-circles'>
                    <div onClick={() => setPageState('main-rsvp')} className={`event-square ${['main-rsvp', 'guest'].includes(pageState) ? 'current-event' : ''} ${guests.every((g) => ['yes', 'no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp) && !(g.name === 'Guest' && responses[`${g.id}_main_rsvp`]?.rsvp === 'yes')) ? 'solid-event' : ''}`}></div>
                    <div className='event-spacer'></div>

                    { invitedEvents.map((eventId, index) => {
                      // find all guests that have this event
                      const guestsWithEvent = guests.filter(guest => guest.events.some(e => e.id === eventId));

                      // find the event details
                      const eventDetails = events.find(e => e.id === eventId);

                      // check if all guests with this event have completed RSVP
                      const allResponded = guestsWithEvent.length > 0 && guestsWithEvent.every(guest => {
                        const key = `${guest.id}_${eventId}`;
                        const resp = responses[key];

                        if (!(resp.rsvp === 'yes' || resp.rsvp === 'no')) return false; // must have RSVP

                        if (eventDetails?.rsvpType === 'meal' && resp.rsvp === 'yes' && guest.type !== 'child') {
                          // meal RSVP must have meal selected
                          return !!resp.meal_id;
                        }

                        return true; // normal RSVP or child
                      });

                      return (
                        <>
                          <div key={`event_circle_${index}`} onClick={() => setEventView(index)} className={`event-circle ${index === currentEvent && pageState === 'rsvp' ? 'current-event' : ''} ${allResponded ? 'solid-event' : ''}`}></div>
                          <div key={`event_circle_spacer_${index}`} className='event-spacer'></div>
                        </>
                      );
                    })}

                    <div onClick={() => { setAdditionalQuestionsView()} } className={`event-square ${['additional-questions'].includes(pageState) ? 'current-event' : ''} ${(lodgingOptionSelected || notBookedYet || reservationRSVPNo) ? 'solid-event' : ''}`}></div>
                  </div>

                  <div className='wedding-rsvp'>
                    <div className='wedding-rsvp-buttons-submit-only'>
                      { readyToSubmit && !reservationRSVPNo ?
                        <button className="btn btn-navyblue" onClick={() => submitAll()}>
                          Submit
                        </button> : <></>
                      }
                    </div>
                  </div>
                </>
              )}
            </div>

            { pageState === 'main-rsvp' && (
              <div className="centered-box wedding-page-block">
                <div className='wedding-rsvp'>
                  <div className="rsvp-card">

                    <div className='rsvp-card-event'>
                      <h4>Will you be attending the wedding weekend?</h4>
                      <h5 className='rsvp-card-date'>April 24 - 25, 2026</h5>
                    </div>

                    {guests.map((guest) => {
                      const key = `${guest.id}_main_rsvp`;
                      const saved = responses[key] || {};

                      const mainRSVP = responses[`${guest.id}_main_rsvp`];
                      const rsvpGoing = mainRSVP?.rsvp !== 'no';

                      return (
                        <div key={guest.id} className="guest-rsvp">
                          <div className='name-and-rsvp'>
                            <p>{guest.name}<span className='pretty-asterisk-small red-asterisk'>*</span>
                              { guest.type == 'guest' ?
                              <svg onClick={() => setPageState('guest')} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil icon-button" viewBox="0 0 16 16">
                                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"></path>
                              </svg> : <></> }
                            </p>

                            <div>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={saved.rsvp === 'yes'}
                                  onChange={() => saveResponse(key, { rsvp: 'yes' })}
                                /> Will Attend
                              </label>
                              <label style={{ marginLeft: 16 }}>
                                <input
                                  type="checkbox"
                                  checked={saved.rsvp === 'no'}
                                  onChange={() => saveResponse(key, { rsvp: 'no' })}
                                /> Will Not Attend
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) }

            { pageState === 'guest' && (
              <div className="centered-box wedding-page-block">
                <div className='wedding-rsvp'>
                  <div className="rsvp-card">
                    <div className='rsvp-card-guest-inputs'>
                      <h4>Update guest name (please use full name)</h4>
                      {guests.map((guest) => {
                        const guestEvent = guest.events.find(e => e.id === currentEventId);
                        if (!guestEvent) return null;

                        const key = `${guest.id}_${currentEventId}`;
                        const saved = responses[key] || {};

                        if (guest.type == 'guest') {
                          return (
                            <div key={guest.id} className="guest-rsvp">
                              <div className='name-and-rsvp'>
                                <input id={`guest_id_${guest.id}_name_input`} type="text" className="form-control name-input" placeholder='Guest' value={guest.name === 'Guest' ? '' : guest.name } onChange={() => saveGuestName()}required />
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) }

            { pageState === 'rsvp' && (
              <div className="centered-box wedding-page-block">
                <div className='wedding-rsvp'>
                  {eventDetails && (
                    <div className="rsvp-card">
                      <div className='rsvp-card-event'>
                        <h4>{eventDetails.name}</h4>
                        <h5 className='rsvp-card-date'>
                          {`${new Date(eventDetails.start).toLocaleString('en-US', {
                                timeZone: 'America/New_York',
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} - ${new Date(eventDetails.end).toLocaleTimeString('en-US', {
                                timeZone: 'America/New_York',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}`} (Eastern Time)
                        </h5>

                        <p className='rsvp-event-location-line'><strong>{eventDetails.location.name}</strong></p>
                        { eventDetails.location.name == 'TBD' ? <></> :
                          <>
                            <p className='rsvp-event-location-line'>{eventDetails.location.line1} {eventDetails.location.line2}</p>
                            <p className='rsvp-event-location-line'>{eventDetails.location.city}, {eventDetails.location.state} {eventDetails.location.zip}</p>
                          </>
                        }
                      </div>

                      {guests.map((guest) => {
                        const guestEvent = guest.events.find(e => e.id === currentEventId);
                        if (!guestEvent) return null;

                        const mainRSVP = responses[`${guest.id}_main_rsvp`];
                        const rsvpGoing = mainRSVP?.rsvp === 'yes';

                        const key = `${guest.id}_${currentEventId}`;
                        const saved = responses[key] || {};

                        return (
                          <div key={guest.id} className={`guest-rsvp ${rsvpGoing ? 'rsvp-yes' : 'rsvp-no' }`}>
                            <div className='name-and-rsvp'>
                              <p>
                                { !rsvpGoing ?
                                <svg onClick={() => setPageState('main-rsvp')} xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-person-fill-add icon-button" style={{marginRight: '10px'}} viewBox="0 0 16 16">
                                  <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0m-2-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0"></path>
                                  <path d="M2 13c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4"></path>
                                </svg> : <></> }
                                {guest.name}
                                <span className='pretty-asterisk-small red-asterisk'>*</span>
                                { guest.type == 'guest' ?
                                <svg onClick={() => setPageState('guest')} hidden={!rsvpGoing} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil icon-button" viewBox="0 0 16 16">
                                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"></path>
                                </svg> : <></> }
                              </p>

                              <div>
                                <label>
                                  <input
                                    disabled={!rsvpGoing}
                                    type="checkbox"
                                    checked={saved.rsvp === 'yes'}
                                    onChange={() => saveResponse(key, { rsvp: 'yes' })}
                                  /> Will Attend
                                </label>
                                <label style={{ marginLeft: 16 }}>
                                  <input
                                    disabled={!rsvpGoing}
                                    type="checkbox"
                                    checked={saved.rsvp === 'no'}
                                    onChange={() => saveResponse(key, { rsvp: 'no' })}
                                  /> Will Not Attend
                                </label>
                              </div>
                            </div>

                            {eventDetails.rsvpType === 'meal' && saved.rsvp === 'yes' && guest.type != 'child' && (
                              <div className='rsvp-meal-options'>
                                <p className='meal-choice-text'>Choose a meal option:</p>
                                {eventDetails.mealOptions.map(meal => {
                                  const isSelected = saved.meal_id === meal.id;
                                  return (
                                    <div
                                      key={meal.id}
                                      onClick={() => saveResponse(key, { meal_id: meal.id })}
                                      className={`meal-option ${isSelected ? 'selected' : ''}`}
                                    >
                                      {/* hidden checkbox for backend consistency */}
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        style={{ display: 'none' }}
                                        readOnly
                                      />
                                      <strong className='rsvp-meal-title'>{meal.name}</strong>
                                      <span className='rsvp-meal-description'>{meal.description}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {eventDetails.rsvpType === 'meal' && saved.rsvp === 'yes' && guest.type == 'child' && (
                              <div className='rsvp-meal-options'>
                                <strong className='rsvp-meal-title'>Children's meals will be selected at the event</strong>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) }

            { pageState === 'additional-questions' && (
              <div className="centered-box wedding-page-block">
                <div className='wedding-rsvp'>
                  {eventDetails && (
                    <div className="rsvp-card">
                      <div className='rsvp-card-event'>
                        <h4>Do you have any dietary restrictions or allergies?</h4>

                        {guests.map((guest) => {
                          const guestEvent = guest.events.find(e => e.id === currentEventId);
                          if (!guestEvent) return null;

                          const mainRSVP = responses[`${guest.id}_main_rsvp`];
                          const rsvpGoing = mainRSVP?.rsvp === 'yes';

                          const key = `${guest.id}_${currentEventId}`;
                          const saved = responses[key] || {};

                          return (
                            <div key={guest.id} className={`guest-rsvp-col ${rsvpGoing ? 'rsvp-yes' : 'rsvp-no' }`}>
                              <div className='name-and-rsvp'>
                                <p>
                                  { !rsvpGoing ?
                                  <svg onClick={() => setPageState('main-rsvp')} xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-person-fill-add icon-button" style={{marginRight: '10px'}} viewBox="0 0 16 16">
                                    <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0m-2-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0"></path>
                                    <path d="M2 13c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4"></path>
                                  </svg> : <></> }
                                  {guest.name}
                                  { guest.type == 'guest' ?
                                  <svg onClick={() => setPageState('guest')} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil icon-button" viewBox="0 0 16 16">
                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"></path>
                                  </svg> : <></> }
                                </p>
                                
                                <textarea value={guest.dietary_restrictions} id={`guest_id_${guest.id}_dietary_restrictions_input`} onChange={ () => saveGuestDietaryRestrictions() } class="form-control dietary-restrictions-textarea" disabled={!rsvpGoing} rows="2" placeholder='Optionally add any allergies or dietary restrictions we should know about' style={{fontStyle: 'italic'}}></textarea>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className='rsvp-card-event'>
                        <h4>Where will you be staying for the wedding weekend?<span className='pretty-asterisk-small red-asterisk'>*</span></h4>
                        {/* LODGING TYPE OPTIONS */}
                        <div className="lodging-options mb-3">
                          {lodgingOptions.map(option => {
                            const isSelected = option.id === reservation?.lodging?.id;

                            return (
                              <div
                                key={option.id}
                                className={`lodging-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setReservation(prev => ({
                                    ...prev,
                                    lodging: {
                                      id: option.id,
                                      name: '',
                                      address: ''
                                    }
                                  }));
                                  setNotBookedYet(false); // unselect "not booked yet"
                                }}
                              >
                                <input type="checkbox" checked={isSelected} readOnly hidden />
                                <strong className="lodging-title">{option.name}</strong>
                              </div>
                            );
                          })}

                          {/* NOT BOOKED YET */}
                          <div
                            key="not-booked-yet"
                            className={`lodging-option full-width-mobile ${notBookedYet ? 'selected' : ''}`}
                            onClick={() => {
                              setReservation(prev => ({
                                ...prev,
                                lodging: {
                                  id: '',
                                  name: '',
                                  address: ''
                                }
                              }));
                              setNotBookedYet(true);
                            }}
                          >
                            <input type="checkbox" checked={notBookedYet} readOnly hidden />
                            <strong className="lodging-title">Not Booked Yet</strong>
                          </div>
                        </div>

                        {/* HOTEL */}
                        {reservation?.lodging?.id === 'hotel' && (
                          <div className="lodging-subform mb-3">
                            <div className="mb-2">
                              <select
                                id="hotel_select"
                                className="form-select"
                                value={
                                  otherHotelSelected ? 'other' : hotelOptions.find(h => h.name === reservation.lodging.name)?.id || ''
                                }
                                onChange={(e) => {
                                  const selectedHotel = hotelOptions.find(
                                    h => h.id === e.target.value
                                  );

                                  if (!selectedHotel) return;

                                  if (selectedHotel.id === 'other') {
                                    setReservation(prev => ({
                                      ...prev,
                                      lodging: {
                                        id: 'hotel',
                                        name: 'Other',
                                        address: ''
                                      }
                                    }));
                                    setOtherHotelSelected(true)
                                  } else {
                                    setReservation(prev => ({
                                      ...prev,
                                      lodging: {
                                        id: 'hotel',
                                        name: selectedHotel.name,
                                        address: selectedHotel.address
                                      }
                                    }));
                                    setOtherHotelSelected(false)
                                  }
                                }}
                              >
                                <option value="">Select a hotel</option>
                                {hotelOptions.map(hotel => (
                                  <option key={hotel.id} value={hotel.id}>
                                    {hotel.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* HOTEL → OTHER */}
                            {reservation.lodging.id === 'hotel' && otherHotelSelected && (
                              <>
                                <div className="mb-2">
                                  <label>Hotel Name<span className='pretty-asterisk-small red-asterisk'>*</span></label>
                                  <input
                                    id="hotel_other_name"
                                    type="text"
                                    className="form-control"
                                    placeholder="Name"
                                    value={reservation.lodging.name === 'Other' ? '' : reservation.lodging.name}
                                    onChange={(e) =>
                                      setReservation(prev => ({
                                        ...prev,
                                        lodging: {
                                          ...prev.lodging,
                                          name: e.target.value
                                        }
                                      }))
                                    }
                                    required
                                  />
                                </div>

                                <div className="mb-2">
                                  <label>Hotel Address<span className='pretty-asterisk-small red-asterisk'>*</span></label>
                                  <input
                                    id="hotel_other_address"
                                    type="text"
                                    className="form-control"
                                    placeholder="Address"
                                    value={reservation.lodging.address}
                                    onChange={(e) =>
                                      setReservation(prev => ({
                                        ...prev,
                                        lodging: {
                                          ...prev.lodging,
                                          address: e.target.value
                                        }
                                      }))
                                    }
                                    required
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* AIRBNB / RENTAL */}
                        {reservation?.lodging?.id === 'rental' && (
                          <div className="lodging-subform mb-3">
                            <div className="mb-2">
                              <label>Rental Address<span className='pretty-asterisk-small red-asterisk'>*</span></label>
                              <input
                                id="rental_address"
                                type="text"
                                className="form-control"
                                placeholder="Address"
                                value={reservation.lodging.address}
                                onChange={(e) =>
                                  setReservation(prev => ({
                                    ...prev,
                                    lodging: {
                                      id: 'rental',
                                      name: 'AirBnB / Rental',
                                      address: e.target.value
                                    }
                                  }))
                                }
                                required
                              />
                            </div>
                          </div>
                        )}

                        {/* OTHER LODGING */}
                        {reservation?.lodging?.id === 'other' && (
                          <div className="lodging-subform mb-3">
                            <div className="mb-2">
                              <label>Location Name<span className='pretty-asterisk-small red-asterisk'>*</span></label>
                              <input
                                id="other_lodging_name"
                                type="text"
                                className="form-control"
                                placeholder="Name"
                                value={reservation.lodging.name}
                                onChange={(e) =>
                                  setReservation(prev => ({
                                    ...prev,
                                    lodging: {
                                      ...prev.lodging,
                                      name: e.target.value
                                    }
                                  }))
                                }
                                required
                              />
                            </div>

                            <div className="mb-2">
                              <label>Location Address<span className='pretty-asterisk-small red-asterisk'>*</span></label>
                              <input
                                id="other_lodging_address"
                                type="text"
                                className="form-control"
                                placeholder="Address"
                                value={reservation.lodging.address}
                                onChange={(e) =>
                                  setReservation(prev => ({
                                    ...prev,
                                    lodging: {
                                      ...prev.lodging,
                                      address: e.target.value
                                    }
                                  }))
                                }
                                required
                              />
                            </div>
                          </div>
                        )}

                        {/* Not Booked Yet */}
                        {notBookedYet && (
                          <div className="lodging-subform">
                            <div className="">
                              <p className='rsvp-info'>See our <Link target="_blank" to="/hotel-and-travel" className='link-to-page'>Hotels</Link> page for links to secure a room in our hotel blocks.</p>
                              <p className='rsvp-info mb-0'>Don't forgt to submit your RSVP now. Once you have booked your stay, come back and edit your RSVP.</p>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) }

            { pageState !== 'reservation-review' && (
              <div className='centered-box wedding-page-block'>
                <div className='wedding-rsvp'>
                  <div className='wedding-rsvp-buttons'>
                    { pageState === 'additional-questions' || (pageState === 'main-rsvp' && reservationRSVPNo) ?
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px'}}>
                        { !readyToSubmit ? <p style={{marginBottom: '0'}}>Complete for all guests and events to submit RSVP</p> : <></> }
                        <button className="btn btn-navyblue" disabled={!readyToSubmit} onClick={() => submitAll()}>
                          Submit
                        </button>
                      </div> : <></>
                    }

                    { pageState !== 'additional-questions' ?
                      <button className="btn btn-navyblue" disabled={!guests.every((g) => ['yes', 'no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp))} hidden={guests.every((g) => ['no'].includes(responses[`${g.id}_main_rsvp`]?.rsvp))} onClick={() => nextButtonClick()}>
                        Next
                      </button> : <></>
                    }

                    { pageState !== 'main-rsvp' ?
                      <button className="btn btn-navyblue" onClick={() => backButtonClick()}>
                        Back
                      </button> : <></>
                    }
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='centered-box'>
            { utils.displayCheckerboard(20, 42, ['green', 'ivory']) }
          </div>
        </div>
      ) }
    </>
  )
};

export default RSVPSubmit;
